/**
 * Money Tools · Overview, "Your financial picture" (TMMAPP-0151 §C; prototype state 29, P:413–454) at
 * `/me/tools`. Every figure is served; nothing is summed in the client except the one sum the spec
 * names (Property & investments: `fixedAssetsMinor + tradableMinor`).
 *
 * - **Net worth:** the reversed feature card, `GET /v1/me/net-worth` `netWorthMinor`, with "Assets less
 *   liabilities · last import {date}" — the latest `lastImport.at` across `GET /v1/me/accounts`, the
 *   clause left out when there is none.
 * - **Tiles**, each opening My Accounts: Cash (`cashMinor`, "{unallocated} unallocated" from
 *   `GET /v1/me/summary`), Credit & lines (`revolvingMinor`, its accounts), Property & investments
 *   (its holdings) and Loans (`loansMinor`, its loans). Owed totals are served positive and read
 *   negative.
 * - **Worth your attention:** open commitments (`GET /v1/me/items?status=OPEN`), the unallocated cash
 *   while it isn't zero, and the next planned item (`summary.nextPlanned`); "Nothing needs you right
 *   now." when none. Each row is a link.
 * - **Tools:** the Money Tools a tier sees (`TOOL_ITEMS`), each with its line.
 * - **Ask about this** (§B) asks about every account the person owns.
 *
 * A GUEST has no books of their own: the money blocks are left out, never shown as zero.
 */
import { useEffect, useState, type ComponentType, type ReactNode } from 'react';
import { Box, Paper, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { accountsApi, type Account } from '@/api/accounts';
import { meetingsApi, type MeetingItem } from '@/api/meetings';
import { netWorthApi, type NetWorth } from '@/api/netWorth';
import { summaryApi, type Summary } from '@/api/summary';
import { ErrorState } from '@/components/ErrorState';
import { MoneyParts } from '@/components/MoneyText';
import { TileMoney, V2Tile } from '@/components/V2Tile';
import {
  CashFlowIcon,
  CommunityNavIcon,
  LockIcon,
  MeetingsIcon,
  PayeesIcon,
  ToolsNavIcon,
  VirtualAccountsIcon,
  type IconProps,
} from '@/components/icons';
import { isOwnerAccount } from '@/features/accounts/accountAccess';
import { AskAboutThis } from '@/features/assistant/AskAboutThis';
import {
  formatCivilDate,
  formatCivilDateNear,
  formatInstant,
  formatMoneyMinor,
} from '@/i18n/format';
import { personName } from '@/i18n/personName';
import { useLocale } from '@/i18n/useLocale';
import { todayCivilInZone } from '@/i18n/zonedCivil';
import { useMe } from '@/session/meHooks';
import { TOOL_ITEMS, visibleTo, type ToolKey } from '@/shell/navigation';
import { PageHeader } from '@/shell/PageHeader';
import { usePageTitle } from '@/shell/usePageTitle';
import { resolveTokens } from '@/theme/theme';
import { tintFor, type TintName } from '@/theme/categorical';

type Block<T> =
  { status: 'loading' } | { status: 'ready'; data: T } | { status: 'error'; error: unknown };

const LOADING = { status: 'loading' } as const;

/** The tools the Overview lists (the Overview itself is the page), with the prototype's glyphs. */
const TOOL_LOOK: Record<
  Exclude<ToolKey, 'overview'>,
  { icon: ComponentType<IconProps>; tint: TintName }
> = {
  accounts: { icon: ToolsNavIcon, tint: 'sage' },
  virtualAccounts: { icon: CommunityNavIcon, tint: 'peach' },
  payees: { icon: PayeesIcon, tint: 'rose' },
  recurring: { icon: MeetingsIcon, tint: 'sky' },
  cashFlow: { icon: CashFlowIcon, tint: 'peach' },
  audit: { icon: VirtualAccountsIcon, tint: 'rose' },
  settings: { icon: LockIcon, tint: 'sage' },
};

/** The owed totals are served positive; Credit & lines and Loans read negative. */
const owed = (minor: number) => 0 - Math.abs(minor);

/** "Sep 15": a served civil date as received, an instant in the viewer's zone. */
function shortDate(value: string, locale: string, timeZone: string): string {
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return formatCivilDate(value, locale, options);
  try {
    return formatInstant(value, locale, timeZone, options);
  } catch {
    return value;
  }
}

/** The latest `lastImport.at` across the accounts, else null. */
function lastImportOf(accounts: readonly Account[]): string | null {
  let latest: string | null = null;
  for (const account of accounts) {
    const at = account.lastImport?.at ?? null;
    if (at !== null && (latest === null || at > latest)) latest = at;
  }
  return latest;
}

/** One served read, with its retry: the read runs again each time `attempt` moves on. */
function useBlock<T>(load: () => Promise<T>, enabled: boolean) {
  const [block, setBlock] = useState<Block<T>>(LOADING);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    load().then(
      (data) => {
        if (!cancelled) setBlock({ status: 'ready', data });
      },
      (error: unknown) => {
        if (!cancelled) setBlock({ status: 'error', error });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [enabled, load, attempt]);
  return {
    block,
    retry: () => {
      setBlock(LOADING);
      setAttempt((n) => n + 1);
    },
  };
}

const loadAccounts = () => accountsApi.list();
const loadNetWorth = () => netWorthApi.get();
const loadSummary = () => summaryApi.get();
const loadItems = () => meetingsApi.listMyItems('OPEN');

/** A content card (visual contract v2 §6): its `h2` in the heading face, then its rows. */
function ContentCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Paper sx={{ p: '20px 22px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <Typography variant="h3" component="h2" sx={{ fontSize: 23, mb: '4px' }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

/** A card row that opens a page: a `line` rule above it, the title over its meta line. */
function RowLink({
  to,
  title,
  sub,
  lead,
}: {
  to: string;
  title: ReactNode;
  sub?: ReactNode;
  lead?: ReactNode;
}) {
  const tokens = resolveTokens(useTheme());
  return (
    <Box
      component={RouterLink}
      to={to}
      data-card-row=""
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        py: lead === undefined ? '12px' : '10px',
        borderTop: '1px solid',
        borderColor: tokens.line,
        color: tokens.text,
        textDecoration: 'none',
        '&:hover [data-row-title]': { textDecoration: 'underline' },
        '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
      }}
    >
      {lead}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
        <Typography
          component="span"
          data-row-title=""
          sx={{ fontWeight: 600, fontSize: 14.5, lineHeight: '20px', overflowWrap: 'anywhere' }}
        >
          {title}
        </Typography>
        {sub !== undefined && (
          <Typography
            component="span"
            sx={{ fontSize: 12.5, lineHeight: '18px', color: tokens.textLabel('surface') }}
          >
            {sub}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export function ToolsOverviewPage() {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const me = useMe();
  const guest = me.tier === 'GUEST';
  const timeZone = me.user.timezone ?? 'UTC';
  const title = usePageTitle({ title: t('toolsOverview.title') });
  const accounts = useBlock(loadAccounts, !guest);
  const netWorth = useBlock(loadNetWorth, !guest);
  const summary = useBlock(loadSummary, !guest);
  const items = useBlock(loadItems, true);

  const accountRows = accounts.block.status === 'ready' ? accounts.block.data : null;
  const owned = accountRows?.filter(isOwnerAccount) ?? null;
  const summaryData = summary.block.status === 'ready' ? summary.block.data : null;
  const operating = summaryData?.operatingCurrency ?? me.user.currency;

  const header = (
    <PageHeader
      title={title}
      subtitle={t('toolsOverview.lead')}
      actions={
        <AskAboutThis
          name={t('toolsOverview.title')}
          scope={
            guest
              ? []
              : (owned?.map((account) => ({ kind: 'ACCOUNT' as const, id: account.id })) ?? null)
          }
        />
      }
    />
  );

  return (
    <Stack sx={{ gap: 3 }}>
      {header}
      {!guest && (
        <NetWorthRow
          netWorth={netWorth}
          summary={summaryData}
          operating={operating}
          lastImport={accountRows === null ? null : lastImportOf(accountRows)}
          timeZone={timeZone}
        />
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
          gap: 2,
          alignItems: 'start',
        }}
      >
        <ContentCard title={t('toolsOverview.attention.title')}>
          <AttentionRows
            items={items}
            summary={guest ? null : summary}
            cashAccounts={
              owned === null
                ? null
                : // The cash accounts the unallocated money sits in: a count, never a sum.
                  owned.filter(
                    (account) =>
                      account.type === 'CASH' &&
                      account.status !== 'CLOSED' &&
                      (account.balances?.unallocatedMinor ?? 0) !== 0,
                  ).length
            }
            operating={operating}
          />
        </ContentCard>
        <ContentCard title={t('toolsOverview.tools.title')}>
          {TOOL_ITEMS.filter(
            (item): item is (typeof TOOL_ITEMS)[number] & { key: Exclude<ToolKey, 'overview'> } =>
              item.key !== 'overview' &&
              visibleTo(item, { subscriber: me.tier === 'SUBSCRIBER', guest }),
          ).map((item) => {
            const look = TOOL_LOOK[item.key];
            const Icon = look.icon;
            return (
              <RowLink
                key={item.key}
                to={item.path}
                title={t(`toolsNav.${item.key}`)}
                sub={t(`toolsOverview.tools.${item.key}`)}
                lead={
                  <Box
                    component="span"
                    aria-hidden
                    sx={{
                      width: 36,
                      height: 36,
                      flex: '0 0 36px',
                      borderRadius: '11px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: tintFor(look.tint, tokens).soft,
                      color: tokens.text,
                    }}
                  >
                    <Icon />
                  </Box>
                }
              />
            );
          })}
        </ContentCard>
      </Box>
    </Stack>
  );
}

function NetWorthRow({
  netWorth,
  summary,
  operating,
  lastImport,
  timeZone,
}: {
  netWorth: ReturnType<typeof useBlock<NetWorth>>;
  summary: Summary | null;
  operating: string | null;
  lastImport: string | null;
  timeZone: string;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  if (netWorth.block.status === 'error') {
    return (
      <ErrorState
        variant="section"
        error={netWorth.block.error}
        what={t('errors.what.netWorth')}
        onRetry={netWorth.retry}
      />
    );
  }
  const data = netWorth.block.status === 'ready' ? netWorth.block.data : null;
  const money = (minor: number | null) => (
    <TileMoney minor={minor} currency={operating} whole tone="total" />
  );
  const loading = <Skeleton variant="text" width={120} />;
  return (
    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }} data-net-worth-row="">
      <Box
        data-net-worth-card=""
        sx={{
          flex: '2 1 280px',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          p: '22px 24px',
          borderRadius: '20px',
          bgcolor: tokens.brand,
          color: tokens.brandInk,
        }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: 12,
            lineHeight: '16px',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: tokens.brandInk,
          }}
        >
          {t('toolsOverview.netWorth')}
        </Typography>
        <Typography
          variant="figureXl"
          component="span"
          sx={{ color: tokens.brandInk, whiteSpace: 'nowrap', overflowWrap: 'normal' }}
        >
          {data === null ? (
            <Skeleton variant="text" width={200} sx={{ bgcolor: tokens.brandHover }} />
          ) : (
            <MoneyParts minor={data.netWorthMinor} currency={operating} whole />
          )}
        </Typography>
        <Typography
          component="span"
          sx={{ fontSize: 13, lineHeight: '18px', color: tokens.brandInk }}
        >
          {lastImport === null
            ? t('toolsOverview.netWorthNote')
            : t('toolsOverview.netWorthNoteImport', {
                date: shortDate(lastImport, locale, timeZone),
              })}
        </Typography>
      </Box>
      <V2Tile
        to="/me/accounts"
        label={t('toolsOverview.tile.cash')}
        value={data === null ? loading : money(data.cashMinor)}
        sub={
          summary === null
            ? undefined
            : t('toolsOverview.tile.cashSub', {
                amount: formatMoneyMinor(summary.unallocatedMinor, operating, locale, false, {
                  whole: true,
                }),
              })
        }
      />
      <V2Tile
        to="/me/accounts"
        label={t('toolsOverview.tile.credit')}
        value={data === null ? loading : money(owed(data.revolvingMinor))}
        sub={
          data === null
            ? undefined
            : t('toolsOverview.tile.creditSub', { count: data.revolving.length })
        }
      />
      <V2Tile
        to="/me/accounts"
        label={t('toolsOverview.tile.property')}
        value={data === null ? loading : money(data.fixedAssetsMinor + data.tradableMinor)}
        sub={
          data === null
            ? undefined
            : t('toolsOverview.tile.propertySub', {
                count: data.fixedAssets.length + data.tradable.length,
              })
        }
      />
      <V2Tile
        to="/me/accounts"
        label={t('toolsOverview.tile.loans')}
        value={data === null ? loading : money(owed(data.loansMinor))}
        sub={
          data === null ? undefined : t('toolsOverview.tile.loansSub', { count: data.loans.length })
        }
      />
    </Box>
  );
}

function AttentionRows({
  items,
  summary,
  cashAccounts,
  operating,
}: {
  items: ReturnType<typeof useBlock<MeetingItem[]>>;
  summary: ReturnType<typeof useBlock<Summary>> | null;
  cashAccounts: number | null;
  operating: string | null;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const today = todayCivilInZone(me.user.timezone ?? 'UTC');
  if (items.block.status === 'loading' || summary?.block.status === 'loading') {
    return (
      <Stack sx={{ gap: 1 }} aria-busy="true">
        <Skeleton variant="rounded" height={40} />
        <Skeleton variant="rounded" height={40} />
      </Stack>
    );
  }
  const rows: ReactNode[] = [];
  if (items.block.status === 'ready') {
    for (const item of items.block.data) {
      if (item.kind !== 'COMMITMENT') continue;
      const owner =
        item.ownerUserId === null && item.ownerDisplayName === null
          ? t('toolsOverview.attention.everyone')
          : personName(item.ownerDisplayName, t);
      rows.push(
        <RowLink
          key={`item-${item.id}`}
          to="/me/meetings"
          title={item.body}
          sub={
            item.dueOn === null
              ? t('toolsOverview.attention.commitmentNoDue', { owner })
              : t('toolsOverview.attention.commitment', {
                  owner,
                  date: formatCivilDateNear(item.dueOn, locale, today),
                })
          }
        />,
      );
    }
  }
  const data = summary?.block.status === 'ready' ? summary.block.data : null;
  if (data !== null && data.unallocatedMinor !== 0) {
    rows.push(
      <RowLink
        key="unallocated"
        to="/me/accounts"
        title={t('toolsOverview.attention.unallocated', {
          amount: formatMoneyMinor(data.unallocatedMinor, operating, locale, false, {
            whole: true,
          }),
        })}
        sub={
          cashAccounts === null || cashAccounts === 0
            ? undefined
            : t('toolsOverview.attention.unallocatedSub', { count: cashAccounts })
        }
      />,
    );
  }
  const next = data?.nextPlanned ?? null;
  if (next !== null) {
    const date = formatCivilDateNear(next.occursOn, locale, today);
    rows.push(
      <RowLink
        key="next"
        to="/me/recurring?tab=transactions"
        title={
          next.payee === null
            ? t('toolsOverview.attention.nextNoPayee', { date })
            : t('toolsOverview.attention.next', { payee: next.payee, date })
        }
        sub={<MoneyParts minor={next.amountMinor} currency={next.currency} signed />}
      />,
    );
  }
  const failed =
    items.block.status === 'error'
      ? items
      : summary !== null && summary.block.status === 'error'
        ? summary
        : null;
  return (
    <>
      {rows}
      {rows.length === 0 && failed === null && (
        <Typography variant="body1" data-attention-empty="" sx={{ py: '12px' }}>
          {t('toolsOverview.attention.empty')}
        </Typography>
      )}
      {failed !== null && failed.block.status === 'error' && (
        <ErrorState
          variant="section"
          error={failed.block.error}
          what={t('errors.what.attention')}
          onRetry={failed.retry}
        />
      )}
    </>
  );
}
